import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './ReportProblemForm.module.css';
import {
  TextField,
  FormHelperText,
  CircularProgress,
  Typography,
  Box,
  Paper,
  Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { DataProviderInstance } from '../../../api/DataProvider';
import { FeedbackConfirmation, Toast } from '../../../components/support';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../components/buttons/CustomButton';
import ColorPalette from '../../../colorpalette/ColorPalette';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

function ReportProblemForm() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { ReportProblem, UploadFeedbackFile } = DataProviderInstance();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    files: []
  });
  
  // Store file previews as data URLs
  const [filePreviews, setFilePreviews] = useState([]);
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const location = useLocation();
  const currentRoute = location.state?.from || window.location.pathname;
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];
    const fileErrors = [];

    selectedFiles.forEach(file => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        fileErrors.push(`O arquivo ${file.name} excede o tamanho máximo de 5MB`);
        return;
      }

      // Check file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        fileErrors.push(`O arquivo ${file.name} não é uma imagem válida (JPEG, PNG ou GIF)`);
        return;
      }

      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors(prev => ({
        ...prev,
        files: fileErrors
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        files: ''
      }));
    }

    // Add new files to the form data
    const newFiles = [...formData.files, ...validFiles];
    setFormData(prev => ({
      ...prev,
      files: newFiles
    }));
    
    // Create data URL previews for the new files
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreviews(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveFile = (index) => {
    // Remove the file and its preview
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
    
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.description.trim()) {
      newErrors.description = 'A descrição do problema é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData object for feedback submission
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('currentRoute', currentRoute);

      // Call API to submit the problem report
      await ReportProblem(submitData, async (response) => {
        if (response.success) {
          // If there are files to upload, handle them
          if (formData.files.length > 0) {
            const feedbackId = response.feedback_id;
            
            // Upload each file
            const fileUploadPromises = formData.files.map(async (file, index) => {
              const fileData = new FormData();
              fileData.append('feedback_id', feedbackId);
              fileData.append('file', file);
              
              return new Promise((resolve) => {
                UploadFeedbackFile(fileData, (fileResponse) => {
                  resolve(fileResponse);
                });
              });
            });
            
            // Wait for all file uploads to complete
            const fileResults = await Promise.all(fileUploadPromises);
            
            // Check if any file uploads failed
            const failedUploads = fileResults.filter(result => !result.success);
            
            if (failedUploads.length > 0) {
              // Some files failed to upload, but the report was submitted
              setToast({
                open: true,
                message: 'Problema reportado, mas alguns arquivos não puderam ser enviados.',
                severity: 'warning'
              });
            } else {
              // All files uploaded successfully
              setSubmitSuccess(true);
              setToast({
                open: true,
                message: 'Problema reportado com sucesso!',
                severity: 'success'
              });
            }
          } else {
            // No files to upload
            setSubmitSuccess(true);
            setToast({
              open: true,
              message: 'Problema reportado com sucesso!',
              severity: 'success'
            });
          }
        } else {
          // Show error toast
          setToast({
            open: true,
            message: response.error || 'Ocorreu um erro ao enviar o formulário. Tente novamente.',
            severity: 'error'
          });
          setErrors({ submit: response.error || 'Ocorreu um erro ao enviar o formulário. Tente novamente.' });
        }
        setIsSubmitting(false);
      });
    } catch (error) {
      // Show error toast
      setToast({
        open: true,
        message: 'Ocorreu um erro ao enviar o formulário. Tente novamente.',
        severity: 'error'
      });
      setErrors({ submit: 'Ocorreu um erro ao enviar o formulário. Tente novamente.' });
      setIsSubmitting(false);
    }
  };

  // Handle toast close
  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast({ ...toast, open: false });
  };

  if (submitSuccess) {
    return (
      <FeedbackConfirmation
        title="Problema reportado com sucesso!"
        message="Agradecemos por nos informar sobre o problema. Nossa equipe irá analisar e entrar em contato se necessário."
        buttonText="Voltar para o Dashboard"
        returnPath="/dashboard"
      />
    );
  }

  return (
    <div className='page-content'>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <AreaTitle 
          Title="Reportar um Problema" 
          Icon={<ReportProblemOutlinedIcon />} 
          Description="Utilize este formulário para reportar problemas ou erros encontrados no sistema. Quanto mais detalhes você fornecer, mais rápido poderemos resolver o problema."
        />
        
        <Divider sx={{ marginTop: '16px', marginBottom: '24px' }} />
        
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup} style={{ marginBottom: '24px' }}>
            <TextField
              label="Título (opcional)"
              name="title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              placeholder="Ex: Erro ao salvar paciente"
            />
          </div>

          <div className={styles.formGroup} style={{ marginBottom: '24px' }}>
            <TextField
              label="Descrição do problema"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              placeholder="Descreva o problema em detalhes, incluindo o que você estava fazendo quando ocorreu"
              required
              error={!!errors.description}
              helperText={errors.description}
            />
          </div>

          <div className={styles.fileUploadContainer} style={{ marginBottom: '32px' }}>
            <Typography className={styles.fileUploadLabel} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
              <CloudUploadIcon style={{ marginRight: '8px' }} />
              Anexar capturas de tela (opcional)
            </Typography>
            <Typography className={styles.fileUploadInfo} style={{ marginBottom: '16px' }}>
              Você pode anexar até 3 imagens (JPEG, PNG ou GIF) com tamanho máximo de 5MB cada
            </Typography>
            
            <CustomButton
              variant="outlined"
              style="secondary"
              label="Selecionar Arquivos"
              icon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current.click()}
              disabled={formData.files.length >= 3}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif"
              multiple
              hidden
              onChange={handleFileChange}
              disabled={formData.files.length >= 3}
            />
            
            {errors.files && (
              <FormHelperText error style={{ marginTop: '8px' }}>
                {Array.isArray(errors.files) ? errors.files.join(', ') : errors.files}
              </FormHelperText>
            )}

            {formData.files.length > 0 && (
              <div className={styles.filePreview} style={{ marginTop: '16px' }}>
                {formData.files.map((file, index) => (
                  <div key={index} className={styles.filePreviewItem}>
                    {filePreviews[index] && (
                      <img
                        src={filePreviews[index]}
                        alt={`Preview ${index}`}
                        className={styles.filePreviewImage}
                      />
                    )}
                    <div
                      className={styles.filePreviewRemove}
                      onClick={() => handleRemoveFile(index)}
                    >
                      <CloseIcon style={{ fontSize: '16px' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {errors.submit && (
            <Box mt={2} mb={2}>
              <Typography color="error">{errors.submit}</Typography>
            </Box>
          )}

          {/* Toast notification */}
          <Toast
            open={toast.open}
            onClose={handleToastClose}
            message={toast.message}
            severity={toast.severity}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CustomButton
              type="submit"
              variant="contained"
              style="primary"
              label={isSubmitting ? <CircularProgress size={24} /> : 'Enviar Relatório'}
              disabled={isSubmitting}
              className={styles.submitButton}
            />
          </div>
        </form>
      </Paper>
    </div>
  );
}

export default ReportProblemForm;